import { useEffect, useState } from 'react'

import {
  ApiConfig,
  ConfigPlan,
  EligibilityPlan,
  EligibilityPlanToDisplay,
  statusResponse,
} from '@/types'
import { useSessionStorage } from 'hooks/useSessionStorage'
import { fetchFromApi } from 'utils/fetch'
import filterEligibility from 'utils/filterEligibility'
import { isMoreThanOneHourAgo } from 'utils/utilsForStorage'

const useFetchEligibility = (
  purchaseAmount: number,
  { domain, merchantId }: ApiConfig,
  plans?: ConfigPlan[],
  customerBillingCountry?: string,
  customerShippingCountry?: string,
  merchantCoversAllFees?: boolean,
): [EligibilityPlanToDisplay[], statusResponse] => {
  const [eligibility, setEligibility] = useState([] as EligibilityPlan[])
  const [status, setStatus] = useState(statusResponse.PENDING)

  // caching
  const { getCache, setCache, createKey, clearCache } = useSessionStorage()
  const key = createKey({
    purchaseAmount,
    plans,
    customerBillingCountry,
    customerShippingCountry,
    merchantCoversAllFees,
    domain,
    merchantId,
  })
  const currentCache = getCache(key)?.value
  const lastCacheTimestamp = getCache(key)?.timestamp
  // If the stored date is more than an hour ago, we should invalidate the cache
  const shouldInvalidate = lastCacheTimestamp && isMoreThanOneHourAgo(lastCacheTimestamp)

  const configInstallments = plans?.map((plan) => ({
    installments_count: plan.installmentsCount,
    deferred_days: plan?.deferredDays,
    deferred_months: plan?.deferredMonths,
  }))
  useEffect(() => {
    if (status === statusResponse.PENDING) {
      let billingAddress = null
      if (customerBillingCountry) {
        billingAddress = {
          country: customerBillingCountry,
        }
      }

      let shippingAddress = null
      if (customerShippingCountry) {
        shippingAddress = {
          country: customerShippingCountry,
        }
      }
      if (currentCache && !shouldInvalidate) {
        setEligibility(currentCache)
        setStatus(statusResponse.SUCCESS)
      }
      if (shouldInvalidate) {
        // If we should invalidate, we clear all cache
        clearCache()
      }
      if (!currentCache) {
        // Fetch eligibility from API
        fetchFromApi(
          {
            purchase_amount: purchaseAmount,
            queries: configInstallments,
            billing_address: billingAddress,
            shipping_address: shippingAddress,
            merchant_covers_all_fees: merchantCoversAllFees,
          },
          {
            Authorization: `Alma-Merchant-Auth ${merchantId}`,
            'X-Alma-Agent': `Alma Widget/${process.env.BUILD_VERSION}`,
          },
          `${domain}/v2/payments/eligibility`,
        )
          .then((res) => {
            // If the response contains an error_code, we set the status to failed - for example if code is 403 unauthorized
            // When purchase amount & plans are not compatible, the API returns an error {"running": {"message": "'purchase_amount'"}} which does not contain the key `error_code`
            // And the widget keeps loading if we don't handle it, so we need to cover this case and returns a FAILED status
            if ('error_code' in res || 'errors' in res) {
              setStatus(statusResponse.FAILED)
            } else {
              setEligibility(res as EligibilityPlan[])
              setCache(key, res as EligibilityPlan[])
              setStatus(statusResponse.SUCCESS)
            }
          })
          .catch(() => {
            setStatus(statusResponse.FAILED)
          })
      }
    }
  }, [
    status,
    purchaseAmount,
    key,
    customerBillingCountry,
    customerShippingCountry,
    currentCache,
    shouldInvalidate,
    configInstallments,
    merchantId,
    domain,
    merchantCoversAllFees,
    setCache,
    clearCache,
  ])
  return [filterEligibility(eligibility, plans), status]
}
export default useFetchEligibility
